/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useEffect, useState } from "react";
import {
  CalendarDaysIcon,
  ChatBubbleLeftIcon,
  HandThumbUpIcon,
} from "@heroicons/react/24/outline";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  HeartIcon,
  BookmarkIcon,
  ShareIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/solid";

// @store
import usePostStore from "../../../store/usePostStore";
import useStatisticStore from "../../../store/useStatisticStore";
import { useSpinnerStore } from "../../../store/useSpinnerStore";
import { useCompanyStore } from "../../../store/useCompanyStore";

// @helpers
import { formatDateExpres } from "../../../helpers/date";

// @config
import routesConfig from "../../../config/routes.config";
import { Post } from "../../../types/post.types";

// @components
import ModalDelet from "../../../components/Modals/modalDelete/ModalDelete";

// @services
import profileService from "../../../services/profile.service";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({ children, className, ...props }) => {
  return (
    <button
      className={`px-4 py-2 rounded-md font-semibold transition-all duration-200 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

interface CardProps {
  children: React.ReactNode;
  className?: string;
}

const Card: React.FC<CardProps> = ({ children, className }) => {
  return (
    <div className={`bg-cBackgroundSecondary rounded-lg shadow-md p-3 ${className}`}>
      {children}
    </div>
  );
};

interface CardContentProps {
  children: React.ReactNode;
}

const CardContent: React.FC<CardContentProps> = ({ children }) => {
  return <div>{children}</div>;
};

const MarketplaceDashboard = () => {
  const { data } = useCompanyStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getPostsByProfessional, deletePost } = usePostStore();
  const { statistics, fetchStatistics } = useStatisticStore();
  const [sortOrder, setSortOrder] = useState("latest");
  const { openSpinner } = useSpinnerStore();
  const [listPost, setListPost] = useState<Post[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [itemSelected, setItemSelected] = useState("");
  const [logo, setLogo] = useState('');
  const [galleryReels, setGalleryReels] = useState<{ id: string, value: Blob }[]>([]);
  const [selectedImage, setSelectedImage] = useState<Post>();

  const getLogoForCompany = async () => {
    if (!data?.companyLogoId) return
    openSpinner(true);
    try {
      await profileService.getLogoForCompany(data?.companyLogoId ?? '').then((response: any) => {
        return setLogo(response);
      })
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      setLogo("");
      toast.error(t("error.callList"));
    }
  }

  const getListPosts = async () => {
    openSpinner(true);
    try {
      if (!data?.professionalId) return;
      const response = await getPostsByProfessional(data.professionalId);
      response?.map((item: Post) => getLogoByPost(item?.postId))
      setListPost(response);
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      toast.error(t("error.callList"));
    }
  }

  const getLogoByPost = async (postId: string) => {
    if (!postId) return;
    openSpinner(true);
    try {
      await profileService.getLogoByPost(postId ?? '').then((response: any) => {
        const list = galleryReels;
        list.push({ id: postId, value: response });
        return setGalleryReels(list);
      })
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      toast.error(t("error.callList"));
    }
  }

  useEffect(() => {
    getLogoForCompany();
    if (data?.professionalId) {
      getListPosts();
      fetchStatistics(data.professionalId ?? '');
    }
  }, [data, fetchStatistics]);

  const handleOpenModal = (id: string) => {
    setItemSelected(id);
    setOpenModal(true);
  }

  const handleDeletePost = async () => {
    openSpinner(true);
    try {
      await deletePost(itemSelected);
      getListPosts();
      setItemSelected("");
      closeModal();
      toast.success(t("professional.marketplace.successDelete"));
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      toast.error(t("error.errorDelete"));
    }
  }

  const handleCreateReels = () => {
    navigate(routesConfig.createPostProfessional);
  }

  const shortText = (text: string) => {
    const newText = text.substring(0, 30);
    return text.length > 30 ? `${newText}...` : text;
  }

  const closeModal = () => setOpenModal(!openModal);

  const getImage = (post: string) => {
    const image = galleryReels?.find((item: any) => item?.id === post);
    if (!image) return "";
    const renderImage = URL.createObjectURL(image.value);
    return renderImage;
  }

  const companyImage = data?.companyImage || `https://eu.ui-avatars.com/api/?name=${data?.name || "User"}+${data?.lastNames || ""}&size=250`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 px-6 text-cText min-h-screen h-[90vh] overflow-auto">
      <div className="col-span-1 lg:col-span-2 bg-cBackground dark:bg-dark-cBackground p-8 rounded-xl shadow-lg w-full max-w-6xl pb-[80px] overflow-auto h-[80vh]">
        <div className="flex flex-col items-center">
          <img
            src={logo ? logo : '/assets/img/no-image.png'}
            alt="Profile"
            className={`w-24 h-24 rounded-full ${logo ? "border-4 border-bgBrand" : ''}`}
          />

          <h1 className="text-xl font-bold mt-4 dark:text-dark-cText">
            {data?.name} {data?.lastNames ?? ''}
          </h1>
          <div className="flex space-x-6 mt-2 text-center gap-[20px]">
            <div>
              <p className="text-lg font-semibold text-bgBrand">
                {statistics?.followers}
              </p>
              <p className="text-cTextSecondary dark:text-dark-cText/60">{t("professional.marketplace.followers")}</p>
            </div>

            <div>
              <p className="text-lg font-semibold text-bgBrand">
                {statistics?.reelsPosted}
              </p>
              <p className="text-cTextSecondary dark:text-dark-cText/60">{t("professional.marketplace.publications")}</p>
            </div>

            <div>
              <p className="text-lg font-semibold text-bgBrand">
                {statistics?.likes}
              </p>
              <p className="text-cTextSecondary dark:text-dark-cText/60">{t("professional.marketplace.likes")}</p>
            </div>
          </div>

          <hr className="flex space-x-6 w-[400px] mt-[20px] h-[2px] bg-[#C4C4C4] border-none" />
        </div>

        <div className="mt-6 w-full flex justify-center">
          <Button className="bg-bgBrand text-white px-6 py-3 rounded-lg shadow-lg"
            onClick={handleCreateReels}>
            {t("professional.marketplace.createReel")}
          </Button>
        </div>

        {listPost?.length
          ? <div className="mt-8 flex justify-end items-center">
            <button
              onClick={() => setSortOrder(sortOrder === "latest" ? "oldest" : "latest")}
              className="text-sm text-bgBrand hover:underline"
            >
              {t("professional.marketplace.order")}
            </button>
          </div>
          : null
        }

        {listPost?.length
          ? <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
            {listPost?.map((post) => (
              <Card key={post?.postId} className="relative dark:bg-[#282828]">
                <CardContent>
                  <div
                    className="h-[200px] w-full"
                    onClick={() => setSelectedImage(post)}
                  >
                    {galleryReels?.length
                      ? <img
                        src={getImage(post?.postId)}
                        alt={post.title}
                        //onError={(e: any) => (e.target.src = avatar)}
                        className="rounded-lg cover h-[200px] w-full object-cover"
                      />
                      : <img
                        src={'/assets/img/no-image.png'}
                        alt={post.title}
                        onError={(e: any) => (e.target.src = '/assets/img/no-image.png')}
                        className="rounded-lg cover h-[200px] w-full object-cover"
                      />
                    }
                  </div>

                  {post?.isActive === false &&
                    <div
                      className="text-[#F97068] bg-white dark:bg-dark-cBackground px-2 py-2 rounded-md flex items-center text-[13px] mt-2 font-semibold"
                    >
                      {t("professional.marketplace.pendingApproval")}
                    </div>
                  }

                  <div className="bg-white dark:bg-dark-cBackground flex p-2 rounded-[7px] mt-2">
                    <div className="w-full flex flex-col">
                      <h3 className="font-semibold text-[14px] dark:text-dark-cText">
                        {shortText(post?.title)}
                      </h3>
                      <h3 className="mt-1 text-[#252525] dark:text-dark-cText/60 text-[14px] text-opacity-50">
                        {shortText(post?.description)}
                      </h3>
                    </div>

                    <div className="w-full flex flex-col justify-end items-end">
                      <div className="flex gap-2">
                        <CalendarDaysIcon className="w-4 h-4 inline dark:text-dark-cText/60" />
                        <p className="text-cTextSecondary dark:text-dark-cText/60 text-[13px]">
                          {formatDateExpres(post?.createdAt)}
                        </p>
                      </div>

                      <div className="flex justify-between items-center mt-2 text-cTextSecondary dark:text-dark-cText/60 text-[13px] gap-[15px]">
                        <span>
                          <HandThumbUpIcon className="w-4 h-4 inline" /> {post?.likes ?? 0}
                        </span>
                        <span><ChatBubbleLeftIcon className="w-4 h-4 inline" /> {post?.comments ?? 0}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleOpenModal(post?.postId)}
                    className="absolute top-4 right-4 bg-[#F97068] text-white px-2 py-1 rounded-md flex items-center gap-2 text-[13px]"
                  >
                    <XCircleIcon className="w-4 h-4" /> {t("professional.marketplace.buttonDelete")}
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
          : <div className="w-full flex justify-center mt-8 text-[15px] text-[#474747] ">
            {t("professional.marketplace.withoutReels")}
          </div>
        }
      </div>

      {/* Mobile Preview */}
      <div className="col-span-1 p-4 h-[80vh]">
        <div className="w-[280px] h-[80vh] max-h-[600px] bg-black rounded-[40px] border-[14px] border-black dark:border-gray-700 shadow-lg mx-auto">
          <div className="relative w-full h-full bg-white dark:bg-dark-cBackground rounded-[26px] overflow-hidden">
            {/* Top Navigation */}
            <div className="absolute top-0 left-0 right-0 z-10">
              <div className={`${selectedImage ? 'flex flex-col w-full' : 'hidden'}`}>
                <div className="flex items-center px-4 pt-2">
                  <button className="p-2">
                    <ArrowLeftIcon className="h-6 w-6 text-white" />
                  </button>
                </div>
                <div className="flex justify-center gap-4 p-3">
                  <button className="text-white/50 font-medium text-[14px]">
                    {t("professional.reels.category")}
                  </button>
                  <button className={`font-medium text-[14px] ${selectedImage ? 'text-[#F97068]' : 'text-[#fff]'}`}>
                    {t("professional.reels.forMy")}
                  </button>
                  <button className="text-white/50 font-medium text-[14px]">
                    {t("professional.reels.favorite")}
                  </button>
                </div>
              </div>
            </div>

            {selectedImage ? (
              <>
                <img
                  //src={selectedImage.image}
                  src={getImage(selectedImage?.postId)}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />

                {/* Interaction buttons column */}
                <div className="absolute bottom-20 right-4 flex flex-col items-center gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-white">
                      <img
                        src={companyImage}
                        alt="Company"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-center">
                    <button className="hover:scale-110 transition-transform">
                      <HeartIcon className="h-7 w-7 text-[#F97068]" />
                    </button>
                    <span className="text-white text-xs">15.4K</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <button className="hover:scale-110 transition-transform">
                      <BookmarkIcon className="h-7 w-7 text-white" />
                    </button>
                    <span className="text-white text-xs">2.1K</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <button className="hover:scale-110 transition-transform">
                      <ShareIcon className="h-7 w-7 text-white" />
                    </button>
                    <span className="text-white text-xs">3.8K</span>
                  </div>
                </div>

                {/* Content overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <h4 className="text-white font-semibold">{selectedImage.title}</h4>
                  <p className="text-white text-sm mt-1">{selectedImage.description}</p>
                  <p className="text-white/80 text-sm mt-2 font-medium">
                    {selectedImage.hashtags}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <p className="flex text-center dark:text-dark-cText/60">
                  {t("professional.reels.uploadImage")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ModalDelet
        open={openModal}
        close={closeModal}
        title={t("professional.marketplace.textDelete")}
        submit={handleDeletePost}
      />
    </div>
  );
};

export default MarketplaceDashboard;
